import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { StatusIcon, getAppointmentStatus } from "components";
import {
  CHECK_EVENT_TYPE,
  COMMUNICATION_EVENTS,
  DESK_COMMUNICATION_EVENTS,
  KEYLOCKER_COMMUNICATION_EVENT_TYPE,
  KEYLOCKER_PIN_REQUEST_TYPES,
  KIOSK_COMMUNICATION_EVENT_TYPES
} from "models";
import { ACSES_COMMUNICATION_EVENT_TYPES } from "models/AcsesCommunication";
import "modules/AppointmentDetails/components/ActivityLog/components/ActivityType/ActivityType.scss";
import { ACTIVITY_LOG_TYPE } from "modules/AppointmentDetails/components/ActivityLog/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { APPOINTMENT_LOG_TYPES, STATUS_IDENTIFIERS } from "models";
import type { EVENT_TYPES } from "modules/AppointmentDetails/components/ActivityLog/utils";

type ActivityTypeProps = {
  activityType: ACTIVITY_LOG_TYPE;
  statusIdentifier?: STATUS_IDENTIFIERS;
  isDeskCheckOut?: boolean;
  event_type?: EVENT_TYPES;
  keylockerPinRequestType?: number;
  activityTitle?: string;
  appointmentLogType?: APPOINTMENT_LOG_TYPES;
};

type KeylockerCommunicationIconAndTitleProps = {
  event_type?: EVENT_TYPES;
  keylockerPinRequestType?: number;
  activityTitle?: string;
};

const CustomerCommunicationIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();
  let title = "";

  switch (event_type) {
    case COMMUNICATION_EVENTS.OnlineCheckInSent:
      title = t("v8_sent_online_checkin", "Sent online checkin");
      break;

    case COMMUNICATION_EVENTS.OnlineCheckInOpened:
      title = t("v8_opened_online_checkin", "Opened online checkin");
      break;

    case COMMUNICATION_EVENTS.OnlineCheckInAnswered:
      title = t("v8_answered_online_checkin", "Answered online checkin");
      break;

    case COMMUNICATION_EVENTS.DiagnoseOverviewSent:
      title = t("v8_sent_diagnose_overview", "Sent diagnose overview");
      break;

    case COMMUNICATION_EVENTS.DiagnoseOverviewOpened:
      title = t("v8_opened_diagnose_overview", "Opened diagnose overview");
      break;

    case COMMUNICATION_EVENTS.DiagnoseOverviewAnswered:
      title = t("v8_answered_diagnose_overview", "Answered diagnose overview");
      break;

    case COMMUNICATION_EVENTS.DiagnoseOverviewResent:
      title = t("v8_resent_diagnose_overview", "Resent diagnose overview");
      break;

    case COMMUNICATION_EVENTS.RepairOverviewSent:
      title = t("v8_sent_repair_overview", "Sent repair overview");
      break;

    case COMMUNICATION_EVENTS.RepairOverviewResent:
      title = t("v8_resent_repair_overview", "Resent repair overview");
      break;

    case COMMUNICATION_EVENTS.RepairOverviewOpened:
      title = t("v8_repair_overview_opened", "Repair overview opened");
      break;

    default:
      return null;
  }

  return (
    <div>
      <Icon className="envelope activity-step-icon-color" /> {title}
    </div>
  );
};

const CheckEventIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();

  switch (event_type) {
    case CHECK_EVENT_TYPE.Started:
      return (
        <div>
          <Icon className="list ul activity-step-icon-color" /> {t("v8_checklist_started", "Checklist started")}
        </div>
      );
    case CHECK_EVENT_TYPE.Resumed:
      return (
        <div>
          <Icon className="circle quarter stroke activity-step-icon-color" /> {t("v8_checklist_resumed", "Checklist resumed")}
        </div>
      );
    case CHECK_EVENT_TYPE.Paused:
      return (
        <div>
          <Icon className="pause activity-step-icon-color" /> {t("v8_checklist_paused", "Checklist paused")}
        </div>
      );
    case CHECK_EVENT_TYPE.Aborted:
      return (
        <div>
          <Icon className="xmark activity-step-icon-color" /> {t("v8_checklist_aborted", "Checklist aborted")}
        </div>
      );
    case CHECK_EVENT_TYPE.Completed:
      return (
        <div>
          <Icon className="list check activity-step-icon-color" /> {t("v8_checklist_completed", "Checklist completed")}
        </div>
      );
    default:
      return null;
  }
};

const DeskCheckinCommunicationIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();
  let title = "";

  switch (event_type) {
    case DESK_COMMUNICATION_EVENTS.DeskCheckInSent:
      title = t("v8_desk_checkin_sent", "Desk check-in sent");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInOpened:
      title = t("v8_desk_checkin_opened", "Desk check-in opened");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInFailedToOpen:
      title = t("v8_desk_checkin_failed_to_open", "Desk check-in failed to open");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInCanceled:
      title = t("v8_desk_checkin_canceled", "Desk check-in canceled");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInReceived:
      title = t("v8_desk_checkin_received", "Desk check-in received");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered:
      title = t("v8_desk_checkin_answered", "Desk check-in answered");
      break;

    default:
      return null;
  }

  return (
    <div>
      <Icon className="tablet activity-step-icon-color" /> {title}
    </div>
  );
};

const DeskCheckoutCommunicationIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();
  let title = "";

  switch (event_type) {
    case DESK_COMMUNICATION_EVENTS.DeskCheckInSent:
      title = t("v8_desk_check_out_sent", "Desk check-out sent");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInOpened:
      title = t("v8_desk_check_out_opened", "Desk check-out opened");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInFailedToOpen:
      title = t("v8_desk_check_out_failed_to_open", "Desk check-out failed to open");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInCanceled:
      title = t("v8_desk_check_out_canceled", "Desk check-out canceled");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInReceived:
      title = t("v8_desk_check_out_received", "Desk check-out received");
      break;

    case DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered:
      title = t("v8_desk_check_out_answered", "Desk check-out answered");
      break;

    default:
      return null;
  }

  return (
    <div>
      <Icon className="tablet activity-step-icon-color" /> {title}
    </div>
  );
};

const KeylockerCommunicationIconAndTitle = ({ event_type, keylockerPinRequestType, activityTitle = "" }: KeylockerCommunicationIconAndTitleProps) => {
  const { t } = useTranslation();
  let title = "";

  switch (event_type) {
    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.CheckInOpened:
      title = t("v8_keylocker_checkin_opened", "Keylocker check-in opened");

      break;

    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.CheckInAnswered:
      title = t("v8_keylocker_checkin_answered", "Keylocker check-in answered");
      break;

    // case KEYLOCKER_COMMUNICATION_EVENT_TYPE.KeylockerCheckInClosed:
    //   title = t("v8_keylocker_checkin_closed", "Keylocker check-in closed");
    //   break;

    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.DropPINSent:
      title =
        keylockerPinRequestType !== KEYLOCKER_PIN_REQUEST_TYPES.TwoFADisabled
          ? t("v8_keylocker_drop_pin_sent", "Keylocker drop pin sent")
          : t("v8_2fa_disabled_no_pin_sent", "2FA disabled, no PIN sent");
      break;

    case KEYLOCKER_COMMUNICATION_EVENT_TYPE.PickUpPINSent:
      title = t("v8_keylocker_pick_up_pin_sent", "Keylocker pick up pin sent");
      break;

    default:
      title = activityTitle;
  }

  return (
    <div>
      <Icon className="key activity-step-icon-color" /> {title}
    </div>
  );
};

const KioskCommunicationIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();
  let title = "";

  switch (event_type) {
    case KIOSK_COMMUNICATION_EVENT_TYPES.CheckInOpened:
      title = t("v8_kiosk_checkin_opened", "Kiosk check-in opened");
      break;

    case KIOSK_COMMUNICATION_EVENT_TYPES.CheckInAnswered:
      title = t("v8_kiosk_checkin_answered", "Kiosk check-in answered");
      break;

    case KIOSK_COMMUNICATION_EVENT_TYPES.LabelDetached:
      title = t("v8_label_detached", "Label detached");
      break;

    default:
      return null;
  }

  return (
    <div>
      <Icon className="tag activity-step-icon-color" /> {title}
    </div>
  );
};

const AcsesReceiverCommunicationIconAndTitle = ({ event_type }: { event_type?: EVENT_TYPES }) => {
  const { t } = useTranslation();

  if (event_type === ACSES_COMMUNICATION_EVENT_TYPES.DropBackPinSent)
    return (
      <div>
        <Icon className="user activity-step-icon-color" /> {t("v8_acses_keylocker_open_by_customer", "Acses keylocker open by customer")}
      </div>
    );

  return null;
};

const WorkOrderIconAndTitle = () => {
  const { t } = useTranslation();

  return (
    <div>
      <Icon className="calendar alternate outline activity-step-icon-color" /> {t("v8_wo_creation_date", "WO creation date")}
    </div>
  );
};

const AppointmentStatusIconAndTitle = ({
  statusIdentifier,
  appointmentLogType
}: {
  statusIdentifier?: STATUS_IDENTIFIERS;
  appointmentLogType?: APPOINTMENT_LOG_TYPES;
}) => {
  const { t } = useTranslation();
  const selectedLocation = useAppointmentLocation();
  const status = getAppointmentStatus(statusIdentifier, selectedLocation?.statuses);

  if (appointmentLogType)
    return (
      <div className="activity-appointment-log-type-icon-container">
        <span className="customer-owner-icon">CO</span>
        {t("v8_new_customer_owner", "New customer owner")}
      </div>
    );

  return (
    <div>
      <StatusIcon status={status.identifier} className="activity-step-icon-color" /> {status.name}
    </div>
  );
};

export const ActivityType = ({
  activityType,
  event_type,
  statusIdentifier,
  isDeskCheckOut,
  keylockerPinRequestType,
  activityTitle,
  appointmentLogType
}: ActivityTypeProps) => {
  switch (activityType) {
    case ACTIVITY_LOG_TYPE.Appointment:
      return <AppointmentStatusIconAndTitle statusIdentifier={statusIdentifier} appointmentLogType={appointmentLogType} />;

    case ACTIVITY_LOG_TYPE.CheckEvent:
      return <CheckEventIconAndTitle event_type={event_type} />;

    case ACTIVITY_LOG_TYPE.Customer:
      return <CustomerCommunicationIconAndTitle event_type={event_type} />;

    case ACTIVITY_LOG_TYPE.Desk:
      if (isDeskCheckOut) return <DeskCheckoutCommunicationIconAndTitle event_type={event_type} />;
      return <DeskCheckinCommunicationIconAndTitle event_type={event_type} />;

    case ACTIVITY_LOG_TYPE.Keylocker:
      return <KeylockerCommunicationIconAndTitle event_type={event_type} keylockerPinRequestType={keylockerPinRequestType} activityTitle={activityTitle} />;

    case ACTIVITY_LOG_TYPE.Kiosk:
      return <KioskCommunicationIconAndTitle event_type={event_type} />;

    case ACTIVITY_LOG_TYPE.AcsesReceiver:
      return <AcsesReceiverCommunicationIconAndTitle event_type={event_type} />;

    case ACTIVITY_LOG_TYPE.WorkOrder:
      return <WorkOrderIconAndTitle />;

    default:
      return null;
  }
};
